// useExplainFlow — E-001: React hook som talar med `ai-flow-explain`
// edge-function. Hämtar lazy (bara vid explicit fetch()-call) och
// exponerar { summary, stepDescriptions, isLoading, error, fetch, reset }.
//
// Samma pattern som useExplainBlock (I-101): state-maskin med
// loading/error/success, JWT-header-passing, fetch-abort på reset.
//
// `buildPayload` bor i `@/lib/explainFlowPayload` så enhetstester kan
// importera den utan att dra in supabase-klienten.

import { useCallback, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  buildPayload,
  type ExplainFlowInput,
  type FlowStepDescription,
} from '@/lib/explainFlowPayload';

export { buildPayload };
export type { ExplainFlowInput, FlowStepDescription };

interface UseExplainFlowResult {
  summary: string;
  stepDescriptions: FlowStepDescription[];
  isLoading: boolean;
  error: string | null;
  fetch: (input: ExplainFlowInput) => Promise<void>;
  reset: () => void;
}

export function useExplainFlow(): UseExplainFlowResult {
  const [summary, setSummary] = useState('');
  const [stepDescriptions, setStepDescriptions] = useState<FlowStepDescription[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setSummary('');
    setStepDescriptions([]);
    setError(null);
    setIsLoading(false);
  }, []);

  const fetchFn = useCallback(async (input: ExplainFlowInput): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setIsLoading(true);
    setError(null);

    try {
      const { data: session } = await supabase.auth.getSession();
      const authToken = session.session?.access_token;
      // S-167: ai-flow-explain-edge-fn:n kräver hård JWT med claims.sub
      // (samma mönster som S-109 för useExplainBlock). Tidigare skickades
      // `apikey`-headern med fel env-var-namn (anon-keyens variant) som
      // fallback men hela appen + .env*-filerna definierar
      // VITE_SUPABASE_PUBLISHABLE_KEY. Headern var alltid tom-string vid
      // runtime → 401 "Missing API key" från edge-runtime, generisk
      // "edge-401"-toast utan handlingsbar feedback. Tidigt-avbrott med
      // "not-authenticated"-state matchar Q-044/S-107/S-109-mönstret.
      if (!authToken) {
        setError('not-authenticated');
        return;
      }
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-flow-explain`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(buildPayload(input)),
        signal: controller.signal,
      });
      if (resp.status === 404) {
        setError('edge-function-not-deployed');
        return;
      }
      if (!resp.ok) {
        const body = await resp.text().catch(() => '');
        setError(`edge-${resp.status}: ${body.slice(0, 120)}`);
        return;
      }
      const data = (await resp.json()) as {
        summary?: string;
        stepDescriptions?: FlowStepDescription[];
        error?: string;
      };
      if (data.error) {
        setError(data.error);
        return;
      }
      setSummary(data.summary ?? '');
      setStepDescriptions(data.stepDescriptions ?? []);
    } catch (e) {
      if ((e as Error).name === 'AbortError') return;
      setError('network-error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { summary, stepDescriptions, isLoading, error, fetch: fetchFn, reset };
}
